import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TextField, IconButton, Popover, List, ListItemButton, ListItemText } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import { format, parse, isValid } from 'date-fns';

/**
 * SimpleTimePicker
 * - Displays a human-friendly 12-hour time (e.g., 1:30 PM)
 * - Emits canonical value as 'HH:mm' (24-hour) via onChange
 * - Supports typing in multiple formats ("1:30pm", "1330", "1p", "13:30")
 * - Includes a compact popover with times at step intervals
 */
export default function SimpleTimePicker({
  label,
  value, // 'HH:mm' or ''
  onChange,
  size = 'small',
  margin = 'dense',
  disabled = false,
  step = 15, // minutes
  minTime = null, // 'HH:mm'
  maxTime = null, // 'HH:mm'
  error = null,
  helperText = null,
  placeholder = 'h:mm AM/PM'
}) {
  const [inputValue, setInputValue] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [inputError, setInputError] = useState(false);
  const buttonRef = useRef(null);

  const displayFormat = 'h:mm a';

  const timeToMinutes = useCallback((hhmm) => {
    if (!hhmm) return null;
    const [h, m] = hhmm.split(':').map(Number);
    if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m;
    return null;
  }, []);

  const clampWithinBounds = useCallback((date) => {
    if (!date) return null;
    const valMin = minTime ? timeToMinutes(minTime) : null;
    const valMax = maxTime ? timeToMinutes(maxTime) : null;
    const cur = date.getHours() * 60 + date.getMinutes();
    if (valMin !== null && cur < valMin) return null;
    if (valMax !== null && cur > valMax) return null;
    return date;
  }, [minTime, maxTime, timeToMinutes]);

  const parseTime = useCallback((str) => {
    if (!str) return null;
    const s = String(str).trim().toLowerCase();
    // quick tokens
    if (s === 'noon') return new Date(2000, 0, 1, 12, 0, 0, 0);
    if (s === 'midnight') return new Date(2000, 0, 1, 0, 0, 0, 0);

    // Try a set of patterns
    const base = new Date(2000, 0, 1);
    const candidates = [
      parse(s, 'H:mm', base),
      parse(s, 'HH:mm', base),
      parse(s, 'h:mm a', base),
      parse(s, 'h:mma', base),
      parse(s, 'ha', base),
      parse(s, 'h a', base),
      // handle 3 or 4 digit military like 930 or 1330
    ];

    // Military-like without colon
    const numeric = s.replace(/[^0-9]/g, '');
    if (numeric.length === 3) {
      // e.g., 930 -> 9:30
      const h = Number(numeric[0]);
      const m = Number(numeric.slice(1));
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) candidates.push(new Date(2000, 0, 1, h, m));
    } else if (numeric.length === 4) {
      // e.g., 1330 -> 13:30
      const h = Number(numeric.slice(0, 2));
      const m = Number(numeric.slice(2));
      if (h >= 0 && h <= 23 && m >= 0 && m <= 59) candidates.push(new Date(2000, 0, 1, h, m));
    }

    // If string ends with 'p' or 'a' without space, coerce
    if (/^\d{1,2}p$/.test(s)) {
      const h = Number(s.replace('p', '')) % 12 + 12;
      candidates.push(new Date(2000, 0, 1, h, 0));
    }
    if (/^\d{1,2}a$/.test(s)) {
      const h = Number(s.replace('a', '')) % 12;
      candidates.push(new Date(2000, 0, 1, h, 0));
    }

    const valid = candidates.find(isValid);
    return valid || null;
  }, []);

  const toHHmm = useCallback((date) => (date ? format(date, 'HH:mm') : ''), []);
  const toDisplay = useCallback((date) => (date ? format(date, displayFormat) : ''), [displayFormat]);

  // Sync input value with external value
  useEffect(() => {
    if (!value) {
      setInputValue('');
      setInputError(false);
      return;
    }
    const dd = parse(value, 'HH:mm', new Date(2000, 0, 1));
    if (isValid(dd)) {
      // Check bounds on prop change
      const bounded = clampWithinBounds(dd);
      setInputValue(toDisplay(dd));
      setInputError(!bounded);
    } else {
      setInputValue(value);
      setInputError(true);
    }
  }, [value, toDisplay, clampWithinBounds]);

  const times = useMemo(() => {
    const items = [];
    const startMin = minTime ? timeToMinutes(minTime) : 0;
    const endMin = maxTime ? timeToMinutes(maxTime) : 23 * 60 + 59;
    // Generate inclusive between bounds, snapping to step
    const first = Math.ceil(startMin / step) * step;
    for (let m = first; m <= endMin; m += step) {
      const h = Math.floor(m / 60);
      const mm = m % 60;
      const date = new Date(2000, 0, 1, h, mm);
      items.push({ value: toHHmm(date), label: toDisplay(date), minutes: m });
    }
    return items;
  }, [minTime, maxTime, step, timeToMinutes, toDisplay, toHHmm]);

  const openMenu = useCallback((e) => {
    if (disabled) return;
    setAnchorEl(e.currentTarget);
  }, [disabled]);
  const closeMenu = useCallback(() => setAnchorEl(null), []);

  const handleSelect = useCallback((val) => {
    onChange?.(val);
    const dd = parse(val, 'HH:mm', new Date(2000, 0, 1));
    setInputValue(toDisplay(dd));
    setInputError(false);
    closeMenu();
  }, [onChange, closeMenu, toDisplay]);

  const handleInputChange = useCallback((e) => {
    const str = e.target.value;
    setInputValue(str);
    if (!str.trim()) {
      setInputError(false);
      onChange?.('');
      return;
    }
    const parsed = parseTime(str);
    if (parsed && clampWithinBounds(parsed)) {
      const snapped = new Date(parsed);
      if (step && step > 1) {
        const mins = snapped.getHours() * 60 + snapped.getMinutes();
        const snappedMin = Math.round(mins / step) * step;
        snapped.setHours(Math.floor(snappedMin / 60), snappedMin % 60, 0, 0);
      }
      setInputError(false);
      onChange?.(toHHmm(snapped));
    } else {
      setInputError(true);
    }
  }, [parseTime, clampWithinBounds, step, toHHmm, onChange]);

  const handleBlur = useCallback(() => {
    if (!value || inputError) return;
    const dd = parse(value, 'HH:mm', new Date(2000, 0, 1));
    if (isValid(dd)) setInputValue(toDisplay(dd));
  }, [value, inputError, toDisplay]);

  const effectiveError = error || inputError;
  const effectiveHelperText = effectiveError ? (helperText || 'Enter a valid time') : (helperText || 'Example: 1:30 PM');

  return (
    <>
      <TextField
        label={label || 'Time'}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleBlur}
        size={size}
        margin={margin}
        disabled={disabled}
        error={!!effectiveError}
        helperText={effectiveHelperText}
        placeholder={placeholder}
        InputProps={{
          endAdornment: (
            <IconButton ref={buttonRef} onClick={openMenu} disabled={disabled} aria-label="Open time options">
              <AccessTime />
            </IconButton>
          )
        }}
        fullWidth
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <List dense sx={{ maxHeight: 320, overflow: 'auto', minWidth: 180 }}>
          {times.map((t) => (
            <ListItemButton key={t.value} selected={value === t.value} onClick={() => handleSelect(t.value)}>
              <ListItemText primary={t.label} />
            </ListItemButton>
          ))}
        </List>
      </Popover>
    </>
  );
}
